import { Box3, Mesh, Object3D, Ray, Raycaster, Vector3, Vector3Like } from 'three'
import store, { AppStore } from '~/store'
import { Wall, WallSegment } from '~/store/objects/types'
import client from '~/client'
import { CARDINALS, DIMENSIONS, FLOOR, POSITIVE_Z } from '~/components/DrawingCanvas/constants/dimensions'
import { useSyncExternalStore } from 'react'
import {
  wallContainingPoint,
  memoizedColumnsMeshes,
  memoizedDoorMeshes,
  memoizedObstructionMeshes,
  memoizedProductMeshes,
  memoizedRoofMeshes,
  memoizedUtilityBoxMeshes,
  memoizedWallMeshes,
} from './collisionMeshes'

export type IntersectionData = [id: string, distance: number, location: Vector3]

class ProductDistanceEngine {
  walls: Readonly<Record<string, Wall>>
  segmentMeshes: Readonly<Map<string, Mesh>>
  segments: Readonly<Record<string, WallSegment>>
  productMeshes: Readonly<Map<string, Mesh>>
  doorClearanceMeshes: Readonly<Map<string, Object3D>>
  obstructionMeshes: Readonly<Map<string, Object3D>>
  utilityBoxMeshes: Readonly<Map<string, Object3D>>
  roofMeshes: Readonly<Map<string, Object3D>>
  columnsMeshes: Readonly<Map<string, Object3D | undefined>>
  raycaster = new Raycaster()
  ray = new Ray()
  origin = new Vector3()
  box = new Box3()
  boxSize = new Vector3(5, 5, 5)
  boxCenter = new Vector3()
  #updateCallbacks: (() => void)[] = []
  snapshot = { productDistanceEngine: this }

  constructor(store: AppStore) {
    const {
      objects,
      segments,
      doors,
      obstructions,
      utilityBoxes,
      roofs,
      roofSections,
      elevationLines,
      elevationPoints,
    } = store.getState().objects.present

    this.walls = objects
    this.segments = segments
    this.segmentMeshes = memoizedWallMeshes(this.segments, this.walls)
    this.productMeshes = new Map()
    this.doorClearanceMeshes = memoizedDoorMeshes(doors, this.segments)
    this.obstructionMeshes = memoizedObstructionMeshes(obstructions, roofs, elevationLines, elevationPoints)
    this.utilityBoxMeshes = memoizedUtilityBoxMeshes(utilityBoxes)
    this.roofMeshes = memoizedRoofMeshes(roofs, elevationLines, elevationPoints)
    this.columnsMeshes = memoizedColumnsMeshes(roofSections)
    memoizedProductMeshes(client, store.getState().objects.present.products).then(it => {
      this.productMeshes = it
    })

    store.subscribe(async () => {
      const {
        objects,
        segments,
        doors,
        obstructions,
        utilityBoxes,
        roofs,
        roofSections,
        elevationLines,
        elevationPoints,
      } = store.getState().objects.present

      this.walls = objects
      this.segments = segments
      this.segmentMeshes = memoizedWallMeshes(this.segments, this.walls)
      this.doorClearanceMeshes = memoizedDoorMeshes(doors, this.segments)
      this.obstructionMeshes = memoizedObstructionMeshes(obstructions, roofs, elevationLines, elevationPoints)
      this.utilityBoxMeshes = memoizedUtilityBoxMeshes(utilityBoxes)
      this.roofMeshes = memoizedRoofMeshes(roofs, elevationLines, elevationPoints)
      this.columnsMeshes = memoizedColumnsMeshes(roofSections)
      this.productMeshes = await memoizedProductMeshes(client, store.getState().objects.present.products)
      this.snapshot = { productDistanceEngine: this }
      this.#updateCallbacks.forEach(it => it())
    })
  }
  isWithinFacility(position: Vector3Like): boolean {
    return wallContainingPoint(position, this.walls, this.segments) !== null
  }
  doSubscribe(callback: () => void): () => void {
    this.#updateCallbacks.push(callback)
    return () => {
      this.#updateCallbacks.splice(this.#updateCallbacks.findIndex(callback), 1)
    }
  }
  subscribeSelector =
    (selector: (engine: ProductDistanceEngine) => unknown) =>
    (callback: () => void): (() => void) => {
      let value = selector(this)
      const filteredCallback = () => {
        const newValue = selector(this)
        if (newValue !== value) {
          value = newValue
          callback()
        }
      }
      return this.doSubscribe(filteredCallback)
    }
  subscribe = (callback: () => void): (() => void) => {
    return this.doSubscribe(callback)
  }
  getSnapshot = () => {
    return this.snapshot
  }
  nearestHeaterWallIntersection(
    pos: Vector3Like,
    boxDimensions: { width: number; depth: number }
  ): [string, number, Vector3] | null {
    this.origin.copy(pos)

    const { depth, width } = boxDimensions

    let distance = Infinity
    const target = new Vector3()
    let objectID: undefined | string = undefined

    this.segmentMeshes.forEach((segment, id) => {
      CARDINALS.forEach(direction => {
        this.raycaster.set(this.origin, direction)
        const isXAxisDirection = direction.y === 0
        const offset = (isXAxisDirection ? width : depth) / 2
        const intersection = this.raycaster.intersectObject(segment)[0]
        if (!intersection || intersection.distance - offset > distance) return
        target.copy(intersection.point)
        distance = intersection.distance
        objectID = id
      })
    })
    if (objectID === undefined) {
      return null
    }
    return [objectID, distance, target]
  }
  nearestWallIntersection(pos: Vector3Like): IntersectionData | null {
    let distance = Infinity
    const target = new Vector3()
    let objectID: undefined | string = undefined

    for (const [id, segment] of this.segmentMeshes) {
      for (const direction of DIMENSIONS) {
        this.origin.copy(pos)
        this.raycaster.set(this.origin, direction)
        let intersection = this.raycaster.intersectObject(segment)[0]
        if (!intersection) {
          this.origin.setZ(FLOOR)
          this.raycaster.set(this.origin, direction)
          intersection = this.raycaster.intersectObject(segment)[0]
        }
        if (!intersection || intersection.distance > distance) continue
        target.copy(intersection.point)
        distance = intersection.distance
        objectID = id
      }
    }
    if (objectID === undefined) return null
    return [objectID, distance, target]
  }
  nearestProductIntersection(pos: Vector3Like, ignoring?: Set<string>): IntersectionData | null {
    this.origin.copy(pos)

    let distance = Infinity
    const target = new Vector3()
    let objectID: undefined | string = undefined

    for (const [id, product] of this.productMeshes) {
      if (ignoring?.has(id)) continue
      const productDistance = this.origin.distanceTo(product.position)
      if (distance > productDistance) {
        objectID = id
        target.copy(product.position)
        distance = productDistance
      }
    }
    if (objectID === undefined) return null
    return [objectID, distance, target]
  }
  nearestObstructionIntersection(pos: Vector3Like): IntersectionData | null {
    const position = new Vector3().copy(pos)
    const ray = new Raycaster(position)

    let distance = Infinity
    const target = new Vector3()
    let objectID: undefined | string = undefined

    for (const meshCollection of [this.doorClearanceMeshes, this.obstructionMeshes, this.utilityBoxMeshes]) {
      meshCollection.forEach((product, id) => {
        DIMENSIONS.forEach(direction => {
          ray.set(position, direction)

          const intersections = ray.intersectObject(product)
          for (const intersection of intersections) {
            if (intersection.distance <= distance) {
              target.copy(intersection.point)
              distance = intersection.distance
              objectID = id
            }
          }
        })
      })
    }

    if (objectID === undefined) {
      return null
    }
    return [objectID, distance, target]
  }
  nearestRoofIntersection(
    pos: Vector3Like,
    productDimensions: { width: number; depth: number }
  ): IntersectionData | null {
    this.origin.copy(pos)

    let distance = Infinity
    const target = new Vector3()
    let objectID: undefined | string = undefined

    const { width, depth } = productDimensions
    for (const [id, roof] of this.roofMeshes) {
      for (const direction of CARDINALS) {
        const isVertical = direction.x === 0
        const offset = (isVertical ? width : depth) / 2
        const raycasterOrigin = direction.clone().multiplyScalar(offset).add(this.origin)
        this.raycaster.set(raycasterOrigin, POSITIVE_Z)
        const intersection = this.raycaster.intersectObject(roof)[0]
        if (!intersection?.face) continue
        const angle = intersection.face.normal.clone().angleTo(POSITIVE_Z)
        const distanceToRoof = Math.cos(angle) * intersection.distance
        if (distanceToRoof > distance) continue
        distance = distanceToRoof
        target.copy(intersection.face.normal).multiplyScalar(intersection.distance).add(raycasterOrigin)
        objectID = id
      }
    }
    if (objectID === undefined) return null
    return [objectID, distance, target]
  }
  getNearestOnAxisProductDistance({
    origin,
    direction,
    ignoring = new Set(),
  }: {
    origin: Vector3Like
    direction: Vector3
    ignoring?: Set<string>
  }) {
    let distance = Infinity
    let productPosition: Vector3 | undefined = undefined
    this.origin.copy(origin).setZ(0)
    const target = new Vector3()

    for (const [id, mesh] of this.productMeshes) {
      if (ignoring.has(id)) continue
      this.boxCenter.copy(mesh.position).setZ(FLOOR)
      this.box.setFromCenterAndSize(this.boxCenter, this.boxSize)
      this.ray.set(this.origin, direction)
      const intersection = this.ray.intersectBox(this.box, target)
      const productDistance = intersection && intersection.distanceTo(this.origin) + this.boxSize.x / 2
      if (!productDistance || productDistance > distance) continue
      distance = productDistance
      productPosition = new Vector3().copy(this.boxCenter)
    }

    return isFinite(distance) && productPosition ? { distance, productPosition } : null
  }
  getNearestCollisionDistance({
    origin,
    direction,
    ignoring = new Set(),
  }: {
    origin: Vector3
    direction: Vector3
    ignoring?: Set<string>
  }) {
    let distance = this.getNearestOnAxisProductDistance({ origin, direction, ignoring })?.distance ?? Infinity
    this.origin.copy(origin)
    this.raycaster.set(this.origin, direction)

    for (const meshCollection of [this.obstructionMeshes, this.segmentMeshes]) {
      meshCollection.forEach((mesh, key) => {
        const intersection = this.raycaster.intersectObject(mesh)[0]
        if (!intersection || intersection.distance > distance || ignoring.has(key)) return
        distance = intersection.distance
      })
    }
    const isZeroIntersections = distance === Infinity
    if (isZeroIntersections) {
      this.origin.setZ(FLOOR)
      this.raycaster.set(this.origin, direction)
      this.segmentMeshes.forEach(mesh => {
        const intersection = this.raycaster.intersectObject(mesh)[0]
        if (!intersection || intersection.distance > distance) return
        distance = intersection.distance
      })
    }
    const isStillZeroIntersections = distance === Infinity
    if (isStillZeroIntersections) return null
    return distance
  }

  getDistanceToMountPoint(pos: Vector3Like): number | undefined {
    this.origin.copy(pos)
    this.origin.setZ(FLOOR)
    this.raycaster.set(this.origin, POSITIVE_Z)
    return this.raycaster.intersectObjects(Array.from(this.roofMeshes.values()))[0]?.distance
  }
  getNearestWallOrColumnPoint(position: Vector3, intersectionOut: Vector3, directionOut: Vector3) {
    this.origin.copy(position)

    let distance = Infinity

    for (const meshCollection of [this.segmentMeshes, this.columnsMeshes]) {
      meshCollection.forEach(mesh => {
        if (!mesh) return
        CARDINALS.forEach(direction => {
          this.raycaster.set(this.origin, direction)
          const intersection = this.raycaster.intersectObject(mesh)[0]
          if (!intersection || intersection.distance > distance) return
          distance = intersection.distance
          intersectionOut.copy(intersection.point)
          directionOut.copy(direction)
        })
      })
    }
    return distance
  }
  getNearestRoofOrWallCollisionDistance(pos: Vector3Like): number {
    this.origin.copy(pos)

    let distance = Infinity

    for (const meshCollection of [this.segmentMeshes, this.roofMeshes]) {
      meshCollection.forEach(mesh => {
        if (!mesh) return
        DIMENSIONS.forEach(direction => {
          this.raycaster.set(this.origin, direction)
          const intersection = this.raycaster.intersectObject(mesh)[0]
          if (!intersection || intersection.distance > distance) return
          distance = intersection.distance
        })
      })
    }
    return distance
  }
}

export const productDistanceEngine = new ProductDistanceEngine(store)

export function useProductDistanceEngine() {
  const { productDistanceEngine: engine } = useSyncExternalStore(
    productDistanceEngine.subscribe,
    productDistanceEngine.getSnapshot
  )
  return engine
}

export function useProductDistanceEngineSelector<T>(selector: (engine: ProductDistanceEngine) => T) {
  const { productDistanceEngine: engine } = useSyncExternalStore(
    productDistanceEngine.subscribeSelector(selector),
    productDistanceEngine.getSnapshot
  )
  return selector(engine)
}
